import { useState, useEffect, useMemo } from 'react'
import { X, CheckCircle, XCircle, AlertCircle, Truck, MapPin, Clock, Edit3, Loader2 } from 'lucide-react'
import api from '@/lib/api'

interface ReturnData {
  id: number
  code: string
  customer_name: string
  customer_email: string
  pickup_address?: {
    street?: string
    city?: string
    postal_code?: string
    country_code?: string
    phone?: string
  } | null
}

interface Props {
  isOpen: boolean
  returnId: number
  returnData?: ReturnData | null
  defaultApproved?: boolean | null
  onClose: () => void
  onSuccess: () => void
}

export default function WarrantyResultModal({ isOpen, returnId, returnData, defaultApproved = null, onClose, onSuccess }: Props) {
  const [approved, setApproved] = useState<boolean | null>(defaultApproved)
  const [rejectionReason, setRejectionReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Shipping fields
  const [shippingName, setShippingName] = useState('')
  const [shippingStreet, setShippingStreet] = useState('')
  const [shippingCity, setShippingCity] = useState('')
  const [shippingPostalCode, setShippingPostalCode] = useState('')
  const [shippingCountryCode, setShippingCountryCode] = useState('ES')
  const [shippingPhone, setShippingPhone] = useState('')
  const [shippingEmail, setShippingEmail] = useState('')
  const [shippingTimeSlot, setShippingTimeSlot] = useState('1')
  const [shippingNotes, setShippingNotes] = useState('')
  const [editingAddress, setEditingAddress] = useState(false)

  // Determine carrier based on postal code
  const carrier = useMemo(() => {
    const cp = (shippingPostalCode || '').replace(/[-\s]/g, '')
    return cp.startsWith('07') ? 'Correos Express' : 'MRW'
  }, [shippingPostalCode])

  useEffect(() => {
    if (isOpen) {
      setApproved(defaultApproved)
      setRejectionReason('')
      setError('')
      setEditingAddress(false)
      setShippingTimeSlot('1')
      setShippingNotes('')

      // Pre-fill from returnData
      if (returnData) {
        setShippingName(returnData.customer_name || '')
        setShippingEmail(returnData.customer_email || '')
        const addr = returnData.pickup_address
        if (addr) {
          setShippingStreet(addr.street || '')
          setShippingCity(addr.city || '')
          setShippingPostalCode(addr.postal_code || '')
          setShippingCountryCode(addr.country_code || 'ES')
          setShippingPhone(addr.phone || '')
        } else {
          setShippingStreet('')
          setShippingCity('')
          setShippingPostalCode('')
          setShippingCountryCode('ES')
          setShippingPhone('')
        }
      }
    }
  }, [isOpen, defaultApproved, returnData])

  const handleSubmit = async () => {
    if (approved === null) return
    if (approved && (!shippingStreet || !shippingCity || !shippingPostalCode)) {
      setError('Completa los datos de direccion de envio')
      return
    }
    if (!approved && !rejectionReason) {
      setError('Indica el motivo del rechazo')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      await api.post(`/returns/admin/${returnId}/warranty-result`, {
        approved,
        ...(approved ? {
          shipping_name: shippingName,
          shipping_street: shippingStreet,
          shipping_city: shippingCity,
          shipping_postal_code: shippingPostalCode,
          shipping_country_code: shippingCountryCode,
          shipping_phone: shippingPhone,
          shipping_email: shippingEmail,
          shipping_time_slot: shippingTimeSlot,
          shipping_notes: shippingNotes || undefined,
        } : {
          rejection_reason: rejectionReason,
        })
      })
      onSuccess()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error procesando resultado')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  const inputBaseClass = "w-full px-3 py-2 border rounded-lg text-sm transition-colors"
  const inputReadonly = `${inputBaseClass} border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 text-slate-700 dark:text-slate-300`
  const inputEditable = `${inputBaseClass} border-green-300 dark:border-green-700 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent`

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Resultado garantia</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Decision */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setApproved(true); setError('') }}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                approved === true ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-green-300'
              }`}
            >
              <CheckCircle className={`w-6 h-6 mx-auto mb-1.5 ${approved === true ? 'text-green-600' : 'text-slate-400'}`} />
              <p className="font-medium text-sm">Aprobar</p>
              <p className="text-xs text-slate-500 mt-0.5">Enviar sustitucion</p>
            </button>
            <button
              onClick={() => { setApproved(false); setError('') }}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                approved === false ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
              }`}
            >
              <XCircle className={`w-6 h-6 mx-auto mb-1.5 ${approved === false ? 'text-red-600' : 'text-slate-400'}`} />
              <p className="font-medium text-sm">Rechazar</p>
              <p className="text-xs text-slate-500 mt-0.5">No procede garantia</p>
            </button>
          </div>

          {/* Approval: shipping form */}
          {approved === true && (
            <div className="space-y-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              {/* Header with edit button */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-medium text-green-900 dark:text-green-200">Direccion de envio</p>
                </div>
                <button
                  onClick={() => setEditingAddress(!editingAddress)}
                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                    editingAddress
                      ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                      : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800/50'
                  }`}
                >
                  <Edit3 className="w-3 h-3" />
                  {editingAddress ? 'Editando' : 'Editar'}
                </button>
              </div>

              {/* Address fields */}
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={shippingName}
                    onChange={(e) => setShippingName(e.target.value)}
                    readOnly={!editingAddress}
                    className={editingAddress ? inputEditable : inputReadonly}
                  />
                </div>
                <div>
                  <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Calle</label>
                  <input
                    type="text"
                    value={shippingStreet}
                    onChange={(e) => setShippingStreet(e.target.value)}
                    readOnly={!editingAddress}
                    className={editingAddress ? inputEditable : inputReadonly}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-green-700 dark:text-green-300 mb-1">CP</label>
                    <input
                      type="text"
                      value={shippingPostalCode}
                      onChange={(e) => setShippingPostalCode(e.target.value)}
                      readOnly={!editingAddress}
                      className={editingAddress ? inputEditable : inputReadonly}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Ciudad</label>
                    <input
                      type="text"
                      value={shippingCity}
                      onChange={(e) => setShippingCity(e.target.value)}
                      readOnly={!editingAddress}
                      className={editingAddress ? inputEditable : inputReadonly}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Pais</label>
                    <input
                      type="text"
                      value={shippingCountryCode}
                      onChange={(e) => setShippingCountryCode(e.target.value)}
                      readOnly={!editingAddress}
                      className={editingAddress ? inputEditable : inputReadonly}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Telefono</label>
                    <input
                      type="text"
                      value={shippingPhone}
                      onChange={(e) => setShippingPhone(e.target.value)}
                      readOnly={!editingAddress}
                      className={editingAddress ? inputEditable : inputReadonly}
                    />
                  </div>
                </div>
                {editingAddress && (
                  <div>
                    <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={shippingEmail}
                      onChange={(e) => setShippingEmail(e.target.value)}
                      className={inputEditable}
                    />
                  </div>
                )}
              </div>

              {/* Carrier (auto-determined, read-only) */}
              <div className="flex items-center gap-3 p-2.5 bg-white dark:bg-slate-700 rounded-lg border border-green-200 dark:border-green-800">
                <Truck className="w-4 h-4 text-green-600 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{carrier}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {carrier === 'Correos Express' ? 'Baleares (CP 07xxx)' : 'Peninsula / Portugal'}
                  </p>
                </div>
              </div>

              {/* Time slot */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  <label className="text-xs font-medium text-green-700 dark:text-green-300">Horario de entrega</label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setShippingTimeSlot('1')}
                    className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                      shippingTimeSlot === '1'
                        ? 'border-green-500 bg-white dark:bg-slate-700 font-medium text-green-800 dark:text-green-200'
                        : 'border-green-200 dark:border-green-800 text-slate-600 dark:text-slate-400 hover:border-green-300'
                    }`}
                  >
                    Manana (08:30-14:00)
                  </button>
                  <button
                    type="button"
                    onClick={() => setShippingTimeSlot('2')}
                    className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                      shippingTimeSlot === '2'
                        ? 'border-green-500 bg-white dark:bg-slate-700 font-medium text-green-800 dark:text-green-200'
                        : 'border-green-200 dark:border-green-800 text-slate-600 dark:text-slate-400 hover:border-green-300'
                    }`}
                  >
                    Tarde (14:00-19:00)
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs text-green-700 dark:text-green-300 mb-1">Observaciones transportista</label>
                <input
                  type="text"
                  value={shippingNotes}
                  onChange={(e) => setShippingNotes(e.target.value.slice(0, 200))}
                  placeholder="Indicaciones para el transportista..."
                  className={inputEditable}
                  maxLength={200}
                />
                {shippingNotes && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5 text-right">{shippingNotes.length}/200</p>
                )}
              </div>
            </div>
          )}

          {/* Rejection fields */}
          {approved === false && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Motivo del rechazo</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                placeholder="Motivo por el que no procede la garantia..."
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm"
              />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-slate-200 dark:border-slate-700 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={approved === null || submitting}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${
              approved === true ? 'bg-green-600 hover:bg-green-700' : approved === false ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600'
            }`}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Generando etiqueta...' : approved === true ? 'Aprobar y generar etiqueta' : approved === false ? 'Rechazar garantia' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
